// Year-scoped festival group pieces (not 2026 hardcoded corps).
// Docs live on iabf-group-pieces-{year}. Rehearsals dual-write to
// iabf-classes-{year} with groupPieceId so itinerary + photoshoot
// conflicts stay linked by id, not title matching.

import { parseTimeToMinutes, formatSlotRange } from './iabf-photoshoot'

export const GROUP_PIECE_DAYS = [
  { key: 'tuesday', label: 'Tuesday', order: 2 },
  { key: 'wednesday', label: 'Wednesday', order: 3 },
  { key: 'thursday', label: 'Thursday', order: 4 },
  { key: 'friday', label: 'Friday', order: 5 }
]

export const GROUP_PIECE_ROOMS = [
  'Harris Theater',
  'Roberson Dance Studio',
  'Urban Amphitheater',
  'Pugh Sculpture Studio',
  'Thompson Field House'
]

export function groupPieceClassId (pieceId, day, startMin) {
  return ['gp', String(pieceId || ''), String(day || ''), String(startMin)].join('-')
}

export function dancerIsInGroupPiece (piece, dancerId) {
  const ids = (piece && piece.allDancerIds) || []
  return !!dancerId && ids.indexOf(dancerId) !== -1
}

export function compactTimeLabel (startHhmm, endHhmm) {
  const a = parseTimeToMinutes(startHhmm)
  const b = parseTimeToMinutes(endHhmm)
  if (a == null || b == null) {
    return ''
  }
  return formatSlotRange(a, b)
}

export function itineraryClassFromGroupRehearsal (piece, rehearsal) {
  const startMin = parseTimeToMinutes(rehearsal && rehearsal.startTime)
  const endMin = parseTimeToMinutes(rehearsal && rehearsal.endTime)
  const day = String((rehearsal && rehearsal.day) || 'tuesday')
  const dayRow = GROUP_PIECE_DAYS.find((d) => d.key === day) || GROUP_PIECE_DAYS[0]
  const title = String((piece && piece.title) || '').trim() || 'Group rehearsal'
  const teacher = String((piece && piece.facultyName) || '').trim()
  const room = String((rehearsal && rehearsal.room) || '').trim()
  const timeLabel = compactTimeLabel(rehearsal && rehearsal.startTime, rehearsal && rehearsal.endTime)
  return {
    id: groupPieceClassId(piece.id, day, startMin || 0),
    day,
    dayOrder: dayRow.order,
    timeLabel,
    startTime: String((rehearsal && rehearsal.startTime) || ''),
    endTime: String((rehearsal && rehearsal.endTime) || ''),
    track: 'rehearsal',
    tracks: ['rehearsal'],
    trackLabel: 'Rehearsal',
    trackLabels: ['Rehearsal'],
    title,
    teacher,
    notes: '',
    description: '',
    defaultRoom: room,
    room,
    hasAccompanist: false,
    order: startMin || 100,
    groupPieceId: piece.id,
    updatedAt: Date.now()
  }
}

export function emptyGroupPiece () {
  return {
    title: '',
    facultyName: '',
    facultyId: '',
    facultyEmail: '',
    facultyUid: '',
    notes: '',
    openForRegistration: true,
    allDancerIds: [],
    dancers: [],
    rehearsals: []
  }
}

function parse24hMinutes (s) {
  return parseTimeToMinutes(s)
}

/** Festival labels like "9-10:45" or "1-2". Hours 1–6 are PM. */
export function parseItineraryLabelRange (label) {
  const m = String(label || '').match(/(\d{1,2})(?::(\d{2}))?\s*-\s*(\d{1,2})(?::(\d{2}))?/)
  if (!m) {
    return null
  }
  const toPm = (h) => (h >= 1 && h <= 6) ? h + 12 : h
  const sh = toPm(parseInt(m[1], 10))
  const sm = m[2] ? parseInt(m[2], 10) : 0
  const eh = toPm(parseInt(m[3], 10))
  const em = m[4] ? parseInt(m[4], 10) : 0
  return { startMin: sh * 60 + sm, endMin: eh * 60 + em }
}

export function classWindow (cls) {
  const start = parse24hMinutes(cls && cls.startTime)
  const end = parse24hMinutes(cls && cls.endTime)
  if (start != null && end != null && end > start) {
    return { startMin: start, endMin: end }
  }
  return parseItineraryLabelRange(cls && cls.timeLabel)
}

export function facultyNameMatchesClass (cls, facultyName) {
  const want = String(facultyName || '').trim().toLowerCase()
  if (!want) {
    return false
  }
  const raw = String((cls && cls.teacher) || '').trim().toLowerCase()
  if (!raw) {
    return false
  }
  if (raw === want) {
    return true
  }
  return raw.split(',').map((s) => s.trim()).indexOf(want) !== -1
}

export function slotKey (day, startMin, endMin) {
  return [day, startMin, endMin].join('|')
}

/**
 * Unique itinerary rows per day (same grid the dancer/faculty schedule uses).
 * Meal / all-track rows are flagged so we don't offer them as rehearsals.
 */
export function itinerarySlotsFromClasses (classes) {
  const byDay = {}
  ;(classes || []).forEach((cls) => {
    const day = String((cls && cls.day) || '')
    const win = classWindow(cls)
    if (!day || !win) {
      return
    }
    const label = String((cls && cls.timeLabel) || '').trim() || compactTimeLabel(
      minutesToHhmm(win.startMin),
      minutesToHhmm(win.endMin)
    )
    const isAll = cls.track === 'all' ||
      (Array.isArray(cls.tracks) && cls.tracks.length === 1 && cls.tracks[0] === 'all')
    if (!byDay[day]) {
      byDay[day] = {}
    }
    const key = label + '|' + win.startMin + '|' + win.endMin
    const prev = byDay[day][key]
    if (!prev) {
      byDay[day][key] = {
        day,
        timeLabel: label,
        startMin: win.startMin,
        endMin: win.endMin,
        startTime: minutesToHhmm(win.startMin),
        endTime: minutesToHhmm(win.endMin),
        isAll
      }
    } else if (isAll) {
      prev.isAll = true
    }
  })
  const days = GROUP_PIECE_DAYS.map((d) => d.key)
  const out = []
  days.forEach((day) => {
    const rows = Object.keys(byDay[day] || {}).map((k) => byDay[day][k])
    rows.sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin)
    rows.forEach((row) => out.push(row))
  })
  return out
}

function minutesToHhmm (total) {
  const n = Math.max(0, Math.round(Number(total) || 0))
  const h = Math.floor(n / 60)
  const m = n % 60
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0')
}

export function facultyBusyWindows (classes, facultyName) {
  const out = []
  ;(classes || []).forEach((cls) => {
    if (!facultyNameMatchesClass(cls, facultyName)) {
      return
    }
    const win = classWindow(cls)
    if (!win) {
      return
    }
    out.push({
      day: cls.day,
      startMin: win.startMin,
      endMin: win.endMin,
      title: String((cls && cls.title) || 'class')
    })
  })
  return out
}

function overlaps (aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd
}

/**
 * Itinerary slots the faculty is free for a group rehearsal.
 * Skips meal/all-track rows. Busy slots include what they're teaching.
 */
export function facultyScheduleOptions (classes, facultyName) {
  const slots = itinerarySlotsFromClasses(classes)
  const busy = facultyBusyWindows(classes, facultyName)
  return slots.filter((slot) => !slot.isAll).map((slot) => {
    const hit = busy.find((b) =>
      b.day === slot.day && overlaps(slot.startMin, slot.endMin, b.startMin, b.endMin)
    )
    return {
      ...slot,
      available: !hit,
      busyTitle: hit ? hit.title : ''
    }
  })
}
